import React from "react";
import {
  hasSignedLiabilityWaiver,
  isInAppMedUpgrade,
} from "@utils/userSegment/features";
import {
  isClinicalRoute,
  isFreemiumClinicalRoute,
  isHRTRoute,
} from "@utils/userSegment";
import { FlexFrame, MainColumn } from "../core/BodyLayout";
import LoggedInHeader from "../header/LoggedInHeader";
import Controls from "../refactored-survey/Controls";
import { usePageBackground } from "src/hooks/usePageBackground";
import { compassColors } from "src/utils/styling";
import styled from "@emotion/styled";
import { PrimaryFocusBlock } from "src/design-system/components/PrimaryFocusBlock/PrimaryFocusBlock";

import { Text } from "src/components/core/Text";
import {
  fontLargeHeadline,
  fontP2Regular,
} from "src/design-system/styles/fonts";
import { useOnce } from "src/hooks/lifecycle";
import { trackBuyflowEvent } from "src/utils/api/tracker";
import { isInsideWebView } from "src/pageDefinitions/goto/utils";
import goto from "src/pageDefinitions/goto";
import RichText from "../RichText";
import i18n from "src/locales";

interface Props {
  children: React.ReactNode;
}

const Container = styled(PrimaryFocusBlock)`
  box-sizing: border-box;
  margin: 26px auto 32px;

  // mobile
  @media only screen and (max-width: 932px) {
    flex-grow: 2;
  }
`;

function LiabilityWaiverReject() {
  useOnce(() => {
    trackBuyflowEvent("NoomMedEligibilityChecked", {
      isEligibleForMed: false,
      medIneligibilityReason: "LIABILITY_WAIVER_SIGNED",
    });
  });

  usePageBackground(compassColors.sky);

  const onCTAClick = () => {
    trackBuyflowEvent("BuyflowButtonClicked", {
      buttonName: "close",
      pageId: "liabilityWaiverReject",
    });

    // Try native exit for in-app scenarios
    if (
      isInsideWebView() &&
      (isInAppMedUpgrade() || isFreemiumClinicalRoute())
    ) {
      goto.nativeWebviewExit();
    }

    // Handle non-webview clinical upgrade
    if (isInAppMedUpgrade()) {
      goto.userPortalHomePage();
      return;
    }

    // Default fallback
    goto.directHome();
  };

  return (
    <FlexFrame>
      <LoggedInHeader />
      <MainColumn margin={24} maxWidth={400} data-cy="liability-waiver-reject">
        <Container>
          <Text
            i18nKeyOrText="t:survey:questions:liabilityWaiverReject:title"
            parent="h1"
            css={fontLargeHeadline}
          />

          <RichText
            css={fontP2Regular}
            translatedParts={i18n.t(
              "survey:questions:liabilityWaiverReject:textParts",
              {
                returnObjects: true,
              }
            )}
          />
        </Container>
        <Controls
          onClickNext={() => {
            onCTAClick();
          }}
          next={{ text: "t:survey:questions:liabilityWaiverReject:cta" }}
          canProceed
        />
      </MainColumn>
    </FlexFrame>
  );
}

export function LiabilityWaiverGate({ children }: Props) {
  const isAttemptingMedicalPurchase =
    isClinicalRoute() || isInAppMedUpgrade() || isHRTRoute();

  if (isAttemptingMedicalPurchase && hasSignedLiabilityWaiver()) {
    return <LiabilityWaiverReject />;
  }

  return <>{children}</>;
}
